
import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { painelSurfaceClass, painelSwitchClass } from '@/components/painel/painel-styles';
import { painelInputClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import { cn } from '@/lib/utils';

type AccountSettingsCardProps = {
  title: string;
  description?: string;
  icon: LucideIcon;
  accentClass?: string;
  children: React.ReactNode;
  delay?: number;
  className?: string;
};

export function AccountSettingsCard({
  title,
  description,
  icon: Icon,
  accentClass = 'bg-sky-500/15 text-sky-300 ring-sky-400/25',
  children,
  delay = 0,
  className,
}: AccountSettingsCardProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      className={cn(painelSurfaceClass, 'overflow-hidden p-0', className)}
    >
      <header className="flex items-start gap-4 border-b border-white/10 bg-gradient-to-r from-white/[0.04] to-transparent px-5 py-4 md:px-6">
        <span
          className={cn(
            'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1',
            accentClass
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-poppins text-lg font-semibold tracking-tight text-white">{title}</h2>
          {description ? (
            <p className="mt-1 max-w-2xl font-inter text-sm leading-relaxed text-slate-400">
              {description}
            </p>
          ) : null}
        </div>
      </header>
      <div className="p-5 md:p-6">{children}</div>
    </motion.section>
  );
}

type AccountFieldProps = {
  id: string;
  label: string;
  children: React.ReactNode;
  hint?: string;
  className?: string;
};

export function AccountField({ id, label, children, hint, className }: AccountFieldProps) {
  return (
    <div className={cn('grid gap-2', className)}>
      <Label htmlFor={id} className="font-inter text-xs font-medium text-blue-100/90">
        {label}
      </Label>
      {children}
      {hint ? <p className="font-inter text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export const accountInputClass = painelInputClass;
export const accountTextareaClass = painelTextareaClass;

type AccountPreferenceRowProps = {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
};

export function AccountPreferenceRow({
  id,
  title,
  description,
  checked,
  onCheckedChange,
  disabled,
}: AccountPreferenceRowProps) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 rounded-xl border border-white/[0.07] bg-white/[0.03] p-4',
        'transition-colors duration-200 hover:border-sky-400/20 hover:bg-white/[0.05]'
      )}
    >
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className="cursor-pointer font-poppins text-sm font-medium text-white">
          {title}
        </Label>
        <p className="mt-1 font-inter text-xs leading-relaxed text-slate-400">{description}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={accountSwitchClass}
      />
    </div>
  );
}

export const accountSwitchClass = painelSwitchClass;

export function AccountSettingsSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="space-y-2">
        <div className="h-8 w-64 rounded-lg bg-white/10" />
        <div className="h-4 w-full max-w-xl rounded bg-white/[0.06]" />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className={cn(painelSurfaceClass, 'h-48 p-6')}>
          <div className="mb-4 h-5 w-40 rounded bg-white/10" />
          <div className="space-y-3">
            <div className="h-10 rounded-xl bg-white/[0.06]" />
            <div className="h-10 rounded-xl bg-white/[0.06]" />
          </div>
        </div>
      ))}
    </div>
  );
}
